import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import BottomNav from '../components/BottomNav';
import SearchBar from '../components/SearchBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function SectionDetailPage() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [section, setSection] = useState(null);
  const [reference, setReference] = useState(null);
  const [provisions, setProvisions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [sectionId]);

  const fetchData = async () => {
    try {
      const sectionResponse = await api.get(`/sections/${sectionId}`);
      setSection(sectionResponse.data);

      const [provisionsResponse, refResponse] = await Promise.all([
        api.get(`/sections/${sectionId}/provisions`),
        api.get(`/references/${sectionResponse.data.reference_id}`),
      ]);

      setProvisions(provisionsResponse.data);
      setReference(refResponse.data);
    } catch (error) {
      toast.error('Failed to load section details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-slate-200 rounded w-1/3"></div>
            <div className="h-6 bg-slate-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!section) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 dark:text-slate-300 dark:hover:bg-slate-700"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        {/* Section Header */}
        <div className="mb-8">
          {reference && (
            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mb-2">{reference.short_name}</p>
          )}
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            {section.number}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400">{section.title}</p>
        </div>

        {/* Search Bar */}
        <SearchBar />

        {/* Provisions */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            Provisions
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            View the complete legal text for each provision. Click on any provision to read the full content and details
          </p>

          {provisions.length === 0 ? (
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-8 text-center text-slate-600 dark:text-slate-400">
                <p>Provisions content will be available soon.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {provisions.map((provision) => (
                <Card
                  key={provision.id}
                  className="hover:shadow-lg transition-all cursor-pointer border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600 dark:bg-slate-800"
                  onClick={() => navigate(`/provisions/${provision.id}`)}
                  data-testid={`provision-${provision.id}`}
                >
                  <CardContent className="p-6">
                    <p className="text-slate-700 dark:text-slate-300 line-clamp-3">{provision.content}</p>
                    <Button
                      className="mt-4 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
                      size="sm"
                      data-testid={`view-provision-${provision.id}`}
                    >
                      Read Full Text
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
